import React, { useState } from 'react'
import Select from 'react-select'
import { Dropdown, Menu } from 'antd'
import { AiOutlineDown } from 'react-icons/ai'

import Receive from '../../components/reusable/receive'
import Patient from '../../components/reusable/patient'
import Confirm from '../../components/reusable/confirm'
import { stringifyDate, useListData, usePet, useToggle } from '../../modules'

import MarkOrderSubmitted from '../../components/drawers/markOrderSubmitted'

const EligibleOrders = () => {
    const [string, setString] = useState('')
    const [selectedInsurer, setSelectedInsurer] = useState({ label: 'All insurers', value: 'all' })
    const [selectedOrder, setSelectedOrder] = useState()
    const [markVisible, , setMarkVisible] = useToggle()

    const [
        { loading, error, fetch },
        { list: orders = [], count = 0 },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('insurerEligibleOrders', {
        string,
        insurer: selectedInsurer?.value !== 'all' ? selectedInsurer?.value : undefined,
    }, { notify: true }, 'orders')

    const [{ data: insurersData }] = usePet('insuranceCompanies', { limit: 100 }, { notify: false })

    const insurerOptions = [
        { label: 'All insurers', value: 'all' },
        ...((insurersData?.insurances ?? []).filter(insurer => !insurer?.isSha).map(insurer => ({
            label: insurer.name,
            value: insurer._id,
        })))
    ]

    const search = str => {
        setString(str)
        navigate(1)
        fetch()
    }

    const filterByInsurer = val => {
        setSelectedInsurer(val)
        navigate(1)
        fetch()
    }

    const startMarkSubmitted = order => {
        setSelectedOrder(order)
        setMarkVisible(true)
    }

    return (
        <div className="w-full mx-auto">
            {
                markVisible &&
                <MarkOrderSubmitted
                    visible={ markVisible }
                    order={ selectedOrder }
                    onClose={ () => {
                        setMarkVisible(false)
                        setSelectedOrder(null)
                    } }
                    onPerform={ fetch } />
            }
            <div className="sm:flex sm:justify-between sm:items-center mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 mb-1">Eligible Orders</h1>
                    <p className="text-sm text-gray-500 mb-0">Open, non-SHA insurance-billed orders that can be submitted for reimbursement.</p>
                </div>
                <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2 mt-3 sm:mt-0">
                    <input
                        onChange={ e => search(e.target.value) }
                        className="appearance-none block bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        type="text"
                        placeholder="Patient Name, Order No." />
                    <div className="w-56">
                        <Select
                            value={ selectedInsurer }
                            onChange={ filterByInsurer }
                            options={ insurerOptions }
                            placeholder="Select insurer" />
                    </div>
                </div>
            </div>
            <div className="col-span-full xl:col-span-8 bg-white shadow-lg rounded-sm border border-gray-200">
                <header className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="font-semibold text-gray-800">Orders</h2>
                    <p className="text-sm text-gray-500 mb-0">{count} order{count === 1 ? '' : 's'}</p>
                </header>
                <div className="p-3">
                    <Receive progress loading={ loading } error={ error }>
                        {
                            orders.length > 0 ?
                                <div className="overflow-x-auto">
                                    <table className="table-auto w-full">
                                        <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                            <tr>
                                                <th className="p-2"><div className="font-semibold text-center">No</div></th>
                                                <th className="p-2"><div className="font-semibold text-left">Patient</div></th>
                                                <th className="p-2"><div className="font-semibold text-left">Payer</div></th>
                                                <th className="p-2"><div className="font-semibold text-left">Order No.</div></th>
                                                <th className="p-2"><div className="font-semibold text-center">Outstanding</div></th>
                                                <th className="p-2"><div className="font-semibold text-center">Opened</div></th>
                                                <th className="p-2"><div className="font-semibold text-center"></div></th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-sm font-medium divide-y divide-gray-100">
                                            {
                                                orders.map((order, key) =>
                                                    <tr key={ order._id }>
                                                        <td className="p-2 text-center">{((page - 1) * limit) + key + 1}.</td>
                                                        <td className="p-2">
                                                            <Patient patient={ order?.patientData } />
                                                        </td>
                                                        <td className="p-2">
                                                            <div>{order?.insurerData?.name ?? '—'}</div>
                                                            <p className="text-gray-500 my-0 text-xs italic">{order?.schemeData?.name ?? 'No scheme'}</p>
                                                        </td>
                                                        <td className="p-2">{order?.orderNo}</td>
                                                        <td className="p-2 text-center">KSh {Number(order?.outstanding ?? 0).toLocaleString()}</td>
                                                        <td className="p-2 text-center">{stringifyDate(order?.date)}</td>
                                                        <td className="p-2 text-center">
                                                            <Dropdown
                                                                overlay={
                                                                    <Menu>
                                                                        <Menu.Item onClick={ () => Confirm({
                                                                            title: 'Mark Order as Submitted?',
                                                                            message: `This will close the order and post KSh ${Number(order?.outstanding ?? 0).toLocaleString()} to ${order?.insurerData?.name ?? 'the insurer'}'s receivable account. Please confirm before proceeding.`,
                                                                            onOk: () => startMarkSubmitted(order),
                                                                            okText: 'Mark Submitted'
                                                                        }) }>Mark Submitted</Menu.Item>
                                                                    </Menu>
                                                                }>
                                                                <button type="button" className="ant-dropdown-link flex items-center h-10" onClick={ e => e.preventDefault() }>
                                                                    <span className="mr-2">Actions</span> <AiOutlineDown />
                                                                </button>
                                                            </Dropdown>
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                                :
                                <div className="w-full h-16 flex items-center justify-center">
                                    <p className="font-semibold text-base">No eligible orders were found</p>
                                </div>
                        }
                    </Receive>
                    {Paginate}
                </div>
            </div>
        </div>
    )
}

export default EligibleOrders
